import React, { useContext, useState } from 'react';
import { Nav, Collapse, Button, ButtonGroup } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  FaHome, 
  FaUserInjured, 
  FaUserMd, 
  FaPills, 
  FaFileMedical,
  FaUsers,
  FaCog,
  FaCalendarCheck,
  FaFileInvoiceDollar,
  FaListOl,
  FaChartLine,
  FaChevronDown,
  FaChevronRight,
  FaDatabase,
  FaMoneyBillWave,
  FaReceipt,
  FaSignOutAlt,
  FaDumbbell,
  FaUserFriends,
  FaClipboardCheck,
  FaCreditCard,
  FaBoxOpen,
  FaRunning,
  FaChartBar
} from 'react-icons/fa';
import { BsQrCodeScan } from 'react-icons/bs';
import { FaRobot } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import { AppModeContext } from '../../context/AppModeContext';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const { appMode, toggleMode } = useContext(AppModeContext);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [gymMembersOpen, setGymMembersOpen] = useState(false);
  const [gymOperationsOpen, setGymOperationsOpen] = useState(false);
  const [gymReportsOpen, setGymReportsOpen] = useState(false);
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  const activeColor = '#1565c0';
  const hoverColor = 'rgba(21, 101, 192, 0.2)';
  
  const permissions = user?.role === 'admin' ? {} : (user?.permissions || {});
  const isAdmin = user?.role === 'admin';
  
  const hasPermission = (key) => {
    return isAdmin || permissions[key] === true;
  };
  
  return (
    <Nav className="flex-column pt-3 sidebar-nav">
      {/* Mode Switch */}
      <div className="px-3 mb-3">
        <ButtonGroup className="w-100">
          <Button
            variant={appMode === 'clinic' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => {
              toggleMode('clinic');
              navigate('/');
            }}
            style={{ fontSize: '0.85rem' }}
          >
            🏥 Clinic
          </Button>
          <Button
            variant={appMode === 'gym' ? 'success' : 'outline-success'}
            size="sm"
            onClick={() => {
              toggleMode('gym');
              navigate('/gym/dashboard');
            }}
            style={{ fontSize: '0.85rem' }}
          >
            🏋️ Gym
          </Button>
        </ButtonGroup>
      </div>
      
      {appMode === 'clinic' ? (
        // Clinic Menu
        <>
          {hasPermission('dashboard') && (
            <Nav.Link 
              as={Link} 
              to="/" 
              className={`py-3 ${location.pathname === '/' ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname === '/' ? activeColor : 'transparent',
                color: location.pathname === '/' ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaHome className="me-2" /> Dashboard
            </Nav.Link>
          )}
          
          {hasPermission('patients') && (
            <Nav.Link 
              as={Link} 
              to="/patients" 
              className={`py-3 ${location.pathname.includes('/patients') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/patients') ? activeColor : 'transparent',
                color: location.pathname.includes('/patients') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaUserInjured className="me-2" /> Patients
            </Nav.Link>
          )}
          
          {hasPermission('doctors') && (
            <Nav.Link 
              as={Link} 
              to="/doctors" 
              className={`py-3 ${location.pathname.includes('/doctors') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/doctors') ? activeColor : 'transparent',
                color: location.pathname.includes('/doctors') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaUserMd className="me-2" /> Doctors
            </Nav.Link>
          )}
          
          {hasPermission('appointments') && (
            <Nav.Link 
              as={Link} 
              to="/appointments" 
              className={`py-3 ${location.pathname.includes('/appointments') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/appointments') ? activeColor : 'transparent',
                color: location.pathname.includes('/appointments') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaCalendarCheck className="me-2" /> Appointments
            </Nav.Link>
          )}
          
          {hasPermission('onlineAppointments') && (
            <Nav.Link 
              as={Link} 
              to="/online-appointments" 
              className={`py-3 ${location.pathname.includes('/online-appointments') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/online-appointments') ? activeColor : 'transparent',
                color: location.pathname.includes('/online-appointments') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaCalendarCheck className="me-2" /> Online Appointments
            </Nav.Link>
          )}
          
          {hasPermission('queue') && (
            <Nav.Link 
              as={Link} 
              to="/queue" 
              className={`py-3 ${location.pathname.includes('/queue') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/queue') ? activeColor : 'transparent',
                color: location.pathname.includes('/queue') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaListOl className="me-2" /> Patient Queue
            </Nav.Link>
          )}
          
          {hasPermission('medicines') && (
            <Nav.Link 
              as={Link} 
              to="/medicines" 
              className={`py-3 ${location.pathname.includes('/medicines') ? 'active-nav-link' : ''}`}
              style={{ 
                backgroundColor: location.pathname.includes('/medicines') ? activeColor : 'transparent',
                color: location.pathname.includes('/medicines') ? '#ffffff' : '#e0e0e0'
              }}
            >
              <FaPills className="me-2" /> Medicines
            </Nav.Link>
          )}
          
          {/* AI Medical Assistant */}
          <Nav.Link 
            as={Link} 
            to="/ai-assistant" 
            className={`py-3 ${location.pathname.includes('/ai-assistant') ? 'active-nav-link' : ''}`}
            style={{ 
              backgroundColor: location.pathname.includes('/ai-assistant') ? activeColor : 'transparent',
              color: location.pathname.includes('/ai-assistant') ? '#ffffff' : '#e0e0e0'
            }}
          >
            <FaRobot className="me-2" /> AI Medical Assistant
          </Nav.Link>
          
          {(hasPermission('reports') || hasPermission('appointmentReports')) && (
            <>
              <div className="border-top border-secondary my-3"></div>
              
              <Nav.Link 
                onClick={() => setReportsOpen(!reportsOpen)}
                className="py-3 d-flex justify-content-between align-items-center"
                style={{ 
                  backgroundColor: 'transparent',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}
              >
                <span>
                  <FaFileMedical className="me-2" /> Medical Reports & Billing
                </span>
                {reportsOpen ? <FaChevronDown /> : <FaChevronRight />}
              </Nav.Link>
              
              <Collapse in={reportsOpen}>
                <div>
                  {hasPermission('reports') && (
                    <Nav.Link 
                      as={Link} 
                      to="/reports" 
                      className={`py-2 ps-4 ${location.pathname === '/reports' ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname === '/reports' ? activeColor : 'transparent',
                        color: location.pathname === '/reports' ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaFileMedical className="me-2" /> Medical Reports
                    </Nav.Link>
                  )}
                  
                  {hasPermission('appointmentReports') && (
                    <Nav.Link 
                      as={Link} 
                      to="/appointment-reports" 
                      className={`py-2 ps-4 ${location.pathname === '/appointment-reports' ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname === '/appointment-reports' ? activeColor : 'transparent',
                        color: location.pathname === '/appointment-reports' ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaCalendarCheck className="me-2" /> Appointment Reports
                    </Nav.Link>
                  )}
                </div>
              </Collapse>
            </>
          )}
          
          {(hasPermission('billing') || hasPermission('deposits') || hasPermission('financialReports') || hasPermission('expenses') || hasPermission('salaries')) && (
            <>
              <Nav.Link 
                onClick={() => setFinanceOpen(!financeOpen)}
                className="py-3 d-flex justify-content-between align-items-center"
                style={{ 
                  backgroundColor: 'transparent',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}
              >
                <span>
                  <FaFileInvoiceDollar className="me-2" /> Finance
                </span>
                {financeOpen ? <FaChevronDown /> : <FaChevronRight />}
              </Nav.Link>
              
              <Collapse in={financeOpen}>
                <div>
                  {hasPermission('billing') && (
                    <Nav.Link 
                      as={Link} 
                      to="/billing" 
                      className={`py-2 ps-4 ${location.pathname.includes('/billing') ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname.includes('/billing') ? activeColor : 'transparent',
                        color: location.pathname.includes('/billing') ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaFileInvoiceDollar className="me-2" /> Billing
                    </Nav.Link>
                  )}
                  
                  {hasPermission('deposits') && (
                    <Nav.Link 
                      as={Link} 
                      to="/doctor-payments" 
                      className={`py-2 ps-4 ${location.pathname === '/doctor-payments' ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname === '/doctor-payments' ? activeColor : 'transparent',
                        color: location.pathname === '/doctor-payments' ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaMoneyBillWave className="me-2" /> Deposit Collection
                    </Nav.Link>
                  )}
                  
                  {hasPermission('financialReports') && (
                    <Nav.Link 
                      as={Link} 
                      to="/financial-reports" 
                      className={`py-2 ps-4 ${location.pathname === '/financial-reports' ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname === '/financial-reports' ? activeColor : 'transparent',
                        color: location.pathname === '/financial-reports' ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaChartLine className="me-2" /> Financial Reports
                    </Nav.Link>
                  )}
                  
                  {hasPermission('expenses') && (
                    <>
                      <Nav.Link 
                        onClick={() => setExpensesOpen(!expensesOpen)}
                        className="py-2 ps-4 d-flex justify-content-between align-items-center"
                        style={{ 
                          backgroundColor: 'transparent',
                          color: '#e0e0e0',
                          cursor: 'pointer'
                        }}
                      >
                        <span>
                          <FaReceipt className="me-2" /> Expenses
                        </span>
                        {expensesOpen ? <FaChevronDown /> : <FaChevronRight />}
                      </Nav.Link>
                      
                      <Collapse in={expensesOpen}>
                        <div>
                          <Nav.Link 
                            as={Link} 
                            to="/expense-types" 
                            className={`py-2 ps-5 ${location.pathname === '/expense-types' ? 'active-nav-link' : ''}`}
                            style={{ 
                              backgroundColor: location.pathname === '/expense-types' ? activeColor : 'transparent',
                              color: location.pathname === '/expense-types' ? '#ffffff' : '#e0e0e0'
                            }}
                          >
                            <FaReceipt className="me-2" /> Expense Types
                          </Nav.Link>
                          
                          <Nav.Link 
                            as={Link} 
                            to="/expenses" 
                            className={`py-2 ps-5 ${location.pathname === '/expenses' ? 'active-nav-link' : ''}`}
                            style={{ 
                              backgroundColor: location.pathname === '/expenses' ? activeColor : 'transparent',
                              color: location.pathname === '/expenses' ? '#ffffff' : '#e0e0e0'
                            }}
                          >
                            <FaReceipt className="me-2" /> Expense Records
                          </Nav.Link>
                        </div>
                      </Collapse>
                    </>
                  )}
                  
                  {hasPermission('salaries') && (
                    <Nav.Link 
                      as={Link} 
                      to="/doctor-salaries" 
                      className={`py-2 ps-4 ${location.pathname === '/doctor-salaries' ? 'active-nav-link' : ''}`}
                      style={{ 
                        backgroundColor: location.pathname === '/doctor-salaries' ? activeColor : 'transparent',
                        color: location.pathname === '/doctor-salaries' ? '#ffffff' : '#e0e0e0'
                      }}
                    >
                      <FaMoneyBillWave className="me-2" /> Doctor Salaries
                    </Nav.Link>
                  )}
                </div>
              </Collapse>
            </>
          )}
          
          {user && user.role === 'admin' && (
            <>
              <div className="border-top border-secondary my-3"></div>
              
              <Nav.Link 
                onClick={() => setAdminOpen(!adminOpen)}
                className="py-3 d-flex justify-content-between align-items-center"
                style={{ 
                  backgroundColor: 'transparent',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}
              >
                <span>
                  <FaCog className="me-2" /> Administration
                </span>
                {adminOpen ? <FaChevronDown /> : <FaChevronRight />}
              </Nav.Link>
              
              <Collapse in={adminOpen}>
                <div>
                  <Nav.Link 
                    as={Link} 
                    to="/users" 
                    className={`py-2 ps-4 ${location.pathname === '/users' ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname === '/users' ? activeColor : 'transparent',
                      color: location.pathname === '/users' ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaUsers className="me-2" /> Users
                  </Nav.Link>
                  
                  <Nav.Link 
                    as={Link} 
                    to="/register" 
                    className={`py-2 ps-4 ${location.pathname === '/register' ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname === '/register' ? activeColor : 'transparent',
                      color: location.pathname === '/register' ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaUsers className="me-2" /> Register User
                  </Nav.Link>
                  
                  <Nav.Link 
                    as={Link} 
                    to="/roles" 
                    className={`py-2 ps-4 ${location.pathname === '/roles' ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname === '/roles' ? activeColor : 'transparent',
                      color: location.pathname === '/roles' ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaUsers className="me-2" /> Manage Roles
                  </Nav.Link>
                  
                  <Nav.Link 
                    as={Link} 
                    to="/settings" 
                    className={`py-2 ps-4 ${location.pathname.includes('/settings') ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname.includes('/settings') ? activeColor : 'transparent',
                      color: location.pathname.includes('/settings') ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaCog className="me-2" /> Settings
                  </Nav.Link>
                  
                  <Nav.Link 
                    as={Link} 
                    to="/backup" 
                    className={`py-2 ps-4 ${location.pathname.includes('/backup') ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname.includes('/backup') ? activeColor : 'transparent',
                      color: location.pathname.includes('/backup') ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaDatabase className="me-2" /> Backup & Restore
                  </Nav.Link>
                </div>
              </Collapse>
            </>
          )}
        </>
      ) : (
        // Gym Menu
        <>
          <Nav.Link 
            as={Link} 
            to="/gym/dashboard" 
            className={`py-3 ${location.pathname === '/gym/dashboard' ? 'active-nav-link' : ''}`}
            style={{ 
              backgroundColor: location.pathname === '/gym/dashboard' ? '#28a745' : 'transparent',
              color: location.pathname === '/gym/dashboard' ? '#ffffff' : '#e0e0e0'
            }}
          >
            <FaHome className="me-2" /> Dashboard
          </Nav.Link>
          
          <div className="border-top border-secondary my-3"></div>
          
          {/* Members Management */}
          <Nav.Link 
            onClick={() => setGymMembersOpen(!gymMembersOpen)}
            className="py-3 d-flex justify-content-between align-items-center"
            style={{ 
              backgroundColor: 'transparent',
              color: '#e0e0e0',
              cursor: 'pointer'
            }}
          >
            <span>
              <FaUserFriends className="me-2" /> Members Management
            </span>
            {gymMembersOpen ? <FaChevronDown /> : <FaChevronRight />}
          </Nav.Link>
          
          <Collapse in={gymMembersOpen}>
            <div>
              <Nav.Link 
                as={Link} 
                to="/gym/members" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/members') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/members') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/members') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaUserFriends className="me-2" /> All Members
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/memberships" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/memberships') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/memberships') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/memberships') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaCreditCard className="me-2" /> Memberships
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/packages" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/packages') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/packages') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/packages') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaBoxOpen className="me-2" /> Packages
              </Nav.Link>
            </div>
          </Collapse>
          
          {/* Daily Operations */}
          <Nav.Link 
            onClick={() => setGymOperationsOpen(!gymOperationsOpen)}
            className="py-3 d-flex justify-content-between align-items-center"
            style={{ 
              backgroundColor: 'transparent',
              color: '#e0e0e0',
              cursor: 'pointer'
            }}
          >
            <span>
              <FaClipboardCheck className="me-2" /> Daily Operations
            </span>
            {gymOperationsOpen ? <FaChevronDown /> : <FaChevronRight />}
          </Nav.Link>
          
          <Collapse in={gymOperationsOpen}>
            <div>
              <Nav.Link 
                as={Link} 
                to="/gym/qr-scanner" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/qr-scanner') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/qr-scanner') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/qr-scanner') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <BsQrCodeScan className="me-2" /> QR Scanner
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/attendance" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/attendance') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/attendance') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/attendance') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaClipboardCheck className="me-2" /> Attendance
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/trainers" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/trainers') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/trainers') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/trainers') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaDumbbell className="me-2" /> Trainers
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/workout-plans" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/workout-plans') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/workout-plans') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/workout-plans') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaRunning className="me-2" /> Workout Plans
              </Nav.Link>
            </div>
          </Collapse>
          
          {/* Finance & Reports */}
          <Nav.Link 
            onClick={() => setGymReportsOpen(!gymReportsOpen)}
            className="py-3 d-flex justify-content-between align-items-center"
            style={{ 
              backgroundColor: 'transparent',
              color: '#e0e0e0',
              cursor: 'pointer'
            }}
          >
            <span>
              <FaChartBar className="me-2" /> Finance & Reports
            </span>
            {gymReportsOpen ? <FaChevronDown /> : <FaChevronRight />}
          </Nav.Link>
          
          <Collapse in={gymReportsOpen}>
            <div>
              <Nav.Link 
                as={Link} 
                to="/gym/payments" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/payments') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/payments') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/payments') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaMoneyBillWave className="me-2" /> Payments
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/expenses" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/expenses') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/expenses') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/expenses') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaReceipt className="me-2" /> Expenses
              </Nav.Link>
              
              <Nav.Link 
                as={Link} 
                to="/gym/reports" 
                className={`py-2 ps-4 ${location.pathname.includes('/gym/reports') ? 'active-nav-link' : ''}`}
                style={{ 
                  backgroundColor: location.pathname.includes('/gym/reports') ? '#28a745' : 'transparent',
                  color: location.pathname.includes('/gym/reports') ? '#ffffff' : '#e0e0e0'
                }}
              >
                <FaChartBar className="me-2" /> Reports
              </Nav.Link>
            </div>
          </Collapse>
          
          {user && user.role === 'admin' && (
            <>
              <div className="border-top border-secondary my-3"></div>
              
              <Nav.Link 
                onClick={() => setAdminOpen(!adminOpen)}
                className="py-3 d-flex justify-content-between align-items-center"
                style={{ 
                  backgroundColor: 'transparent',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}
              >
                <span>
                  <FaCog className="me-2" /> Administration
                </span>
                {adminOpen ? <FaChevronDown /> : <FaChevronRight />}
              </Nav.Link>
              
              <Collapse in={adminOpen}>
                <div>
                  <Nav.Link 
                    as={Link} 
                    to="/users" 
                    className={`py-2 ps-4 ${location.pathname === '/users' ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname === '/users' ? '#28a745' : 'transparent',
                      color: location.pathname === '/users' ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaUsers className="me-2" /> Users
                  </Nav.Link>
                  
                  <Nav.Link 
                    as={Link} 
                    to="/settings" 
                    className={`py-2 ps-4 ${location.pathname.includes('/settings') ? 'active-nav-link' : ''}`}
                    style={{ 
                      backgroundColor: location.pathname.includes('/settings') ? '#28a745' : 'transparent',
                      color: location.pathname.includes('/settings') ? '#ffffff' : '#e0e0e0'
                    }}
                  >
                    <FaCog className="me-2" /> Settings
                  </Nav.Link>
                </div>
              </Collapse>
            </>
          )}
        </>
      )}
      
      <div className="border-top border-secondary my-3 d-md-none"></div>
      
      <Nav.Link 
        onClick={handleLogout}
        className="py-3 d-md-none"
        style={{ 
          backgroundColor: 'transparent',
          color: '#ef4444',
          cursor: 'pointer',
          fontWeight: '600'
        }}
      >
        <FaSignOutAlt className="me-2" /> Logout
      </Nav.Link>
    </Nav>
  );
};

export default Sidebar;
